import {captureException} from '@sentry/browser';
import {Button, DataStatus} from 'platform/components';
import {Center, Show, VStack} from 'platform/foundation';

import {isEmpty, isNil} from 'ramda';

import {usePostOAuthTokenMutation} from '@dms/api/base';
import {browserStorageKey} from '@dms/config';
import i18n from '@dms/i18n';
import {
  buildUrl,
  dashboardRoutes,
  getWorkspaceFromUri,
  iamQueryParams,
  iamRedirectRoute,
  redirectToLogin,
} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, useNavigate, useOnMount} from 'shared/web';

import {getRedirectSearchParams} from './utils/getRedirectSearchParams';

export const IamRedirect = () => {
  const [postOAuthToken, {isError}] = usePostOAuthTokenMutation();
  const {workspace} = getWorkspaceFromUri();
  const navigate = useNavigate();

  const [areSearchParamsValid, params] = getRedirectSearchParams();

  useOnMount(() => {
    if (!areSearchParamsValid) {
      captureException(new Error('FATAL: Invalid redirect search params'), {extra: {params}});
      return;
    }

    const redirect_uri = `${window.location.origin}${window.location.pathname}?${iamQueryParams.ORIGIN_URI}=${encodeURIComponent(params?.origin_href)}`;

    postOAuthToken({
      grant_type: 'authorization_code',
      code: params.code,
      redirect_uri,
      client_id: 'dms',
    })
      .unwrap()
      .then((response) => {
        sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, response.access_token);
        if (response.refresh_token) {
          localStorage.setItem(browserStorageKey.REFRESH_TOKEN, response.refresh_token);
        }

        if (isNil(params?.origin_href) || isEmpty(params?.origin_href)) {
          navigate(dashboardRoutes.home, {replace: true});
          return;
        }

        const redirectUrl = new URL(params?.origin_href);
        navigate(composePath(redirectUrl.pathname, {queryParams: redirectUrl.search}), {
          replace: true,
        });
      })
      .catch(handleApiError);
  });

  return (
    <Center height="100vh" width="100%">
      <VStack spacing={4}>
        <DataStatus
          isLoading
          isError={isError || !areSearchParamsValid}
          errorMessage={
            areSearchParamsValid
              ? i18n.t('general.errors.invalidLoginAttempt')
              : i18n.t('general.errors.invalidParams')
          }
        />

        <Show when={isError || !areSearchParamsValid}>
          <Button
            title={i18n.t('page.login.actions.backToLogin')}
            onClick={() =>
              redirectToLogin({
                redirectUrl: `${buildUrl(iamRedirectRoute, workspace, false)}?${iamQueryParams.ORIGIN_URI}=${encodeURIComponent(window.location.origin)}`,
              })
            }
          />
        </Show>
      </VStack>
    </Center>
  );
};
